/** Estado de sessao persistido e helpers de autorizacao na interface. */

import type { PermissionProfile, Role, Session, User } from './types.js';
import { setAuthToken } from './api.js';

const STORAGE_KEY = 'f2f.session.v2';

export interface SessionState {
  session: Session | null;
}

const state: SessionState = { session: null };

const listeners = new Set<(session: Session | null) => void>();

export function loadStoredSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (!parsed?.token || !parsed?.user) return null;
    state.session = parsed;
    setAuthToken(parsed.token);
    return parsed;
  } catch {
    return null;
  }
}

export function setSession(session: Session | null): void {
  state.session = session;
  setAuthToken(session?.token ?? null);

  if (session) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }

  for (const listener of listeners) listener(session);
}

export function getSession(): Session | null {
  return state.session;
}

export function getUser(): User | null {
  return state.session?.user ?? null;
}

export function getPermissions(): PermissionProfile | null {
  return state.session?.permissions ?? null;
}

export function getRole(): Role | null {
  return state.session?.user.role ?? null;
}

export function onSessionChange(listener: (session: Session | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Helpers de visibilidade usados pelo menu e pelas telas. */
export const can = {
  viewTemplates: (): boolean => (getPermissions()?.templates ?? 'none') !== 'none',
  manageTemplates: (): boolean => getPermissions()?.templates === 'manage',
  createEditions: (): boolean => {
    const value = getPermissions()?.editions;
    return value === 'manage' || value === 'create';
  },
  manageEditions: (): boolean => getPermissions()?.editions === 'manage',
  writeRepository: (): boolean => (getPermissions()?.repository ?? 'none') !== 'none',
  manageUsers: (): boolean => (getPermissions()?.userManagement ?? 'none') !== 'none',
  viewAudit: (): boolean => getPermissions()?.visibility === 'global',
  incorporateOfficial: (): boolean => getPermissions()?.canIncorporateOfficialModel === true,
  isMinistrador: (): boolean => getPermissions()?.repository === 'own',
};
